import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, ShieldCheck, Sparkles, Trophy } from "lucide-react";
import { authHeader, getUserToken } from "@/utils/userAuth";
import { extractApiError } from "@/utils/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Mini sales landing for Lezione 2 / Lezione 3.                              */
/*  · Hidden from public navigation — accessible only via admin magic-link.   */
/*  · Personalized greeting using the user's first name (read from /users/me).*/
/*  · CTA dispatches POST /api/checkout/lesson-{2|3} → redirect to Stripe.    */
/* -------------------------------------------------------------------------- */

const LESSON_COPY = {
  "lezione-2": {
    number: 2,
    previousNumber: 1,
    priceLabel: "€2",
    checkoutEndpoint: "/checkout/lesson-2",
    eyebrow: "Lezione 2 sbloccata",
    titleLine1: "Hai completato",
    titleHighlight: "la Lezione 1.",
    titleLine2: "Complimenti.",
    subtitle:
      "Pochissimi arrivano fin qui. Il fatto che tu sia ancora con noi significa che hai capito una cosa che molti non vedono.",
    recapTitle: "Nella lezione precedente abbiamo visto",
    recapBody: (
      <>
        Il primo punto: non stai vivendo solo singole crisi —{" "}
        <strong>stai vivendo un copione</strong> che si ripete.
      </>
    ),
    bridgeBody: (
      <>
        Ma come è possibile che questo copione si ripeta ogni volta,{" "}
        <em>anche quando una parte di te sa già come va a finire?</em>
      </>
    ),
    answerBody: (
      <>
        Grazie al <strong>sistema di influenza</strong> che ogni copione attiva.
      </>
    ),
    promiseTitle: "In questa lezione tratteremo",
    promiseHighlight: "il modello a 6 assi",
    promisePayoff:
      "Cosa ci porta veramente a ripetere il copione — anche quando lo riconosciamo.",
    ctaLabel: "Sblocca la Lezione 2 (€2)",
  },
  "lezione-3": {
    number: 3,
    previousNumber: 2,
    priceLabel: "€10",
    checkoutEndpoint: "/checkout/lesson-3",
    eyebrow: "Lezione 3 sbloccata",
    titleLine1: "Hai completato",
    titleHighlight: "la Lezione 2.",
    titleLine2: "Sei sulla strada giusta.",
    subtitle:
      "Hai visto il meccanismo. Ora è il momento di capire dove agire per rompere lo schema.",
    recapTitle: "Nella seconda lezione abbiamo visto",
    recapBody: (
      <>
        Come <strong>il copione ti trascina dentro</strong> ogni volta, anche
        quando riconosci i segnali.
      </>
    ),
    bridgeBody: (
      <>
        In questa lezione andiamo ancora più a fondo nel{" "}
        <strong>modello a 6 assi</strong>.
      </>
    ),
    answerBody: (
      <>
        E spieghiamo <em>su cosa agiscono</em> questi assi.
      </>
    ),
    promiseTitle: "In questa lezione scoprirai",
    promiseHighlight: "la cornice",
    promisePayoff:
      "Il livello su cui agiscono i 6 assi — e perché senza vederlo nessuna tecnica funziona davvero.",
    ctaLabel: "Sblocca la Lezione 3 (€10)",
  },
  "corso-completo": {
    number: "full",
    accessKey: "has_access_full_course",
    priceLabel: "€197",
    checkoutEndpoint: "/checkout/full-course",
    eyebrow: "Offerta riservata",
    titleLine1: "Hai visto",
    titleHighlight: "la prima lezione.",
    titleLine2: "Ora puoi avere il corso completo.",
    subtitle:
      "Tutto il metodo Neuroscienza Pratica — Figlio a Carico, applicabile dalla prima settimana, con garanzia totale 60 giorni e accesso immediato a tutte le 9 lezioni.",
    recapTitle: "Nella prima lezione hai visto",
    recapBody: (
      <>
        Il primo punto: non stai vivendo solo singole crisi —{" "}
        <strong>stai vivendo un copione</strong> che si ripete.
      </>
    ),
    bridgeBody: (
      <>
        Ora hai bisogno del <strong>resto del metodo</strong> — le tecniche, le sequenze,
        gli esercizi da applicare passo dopo passo nella tua situazione concreta.
      </>
    ),
    answerBody: (
      <>
        Con il <em>corso completo</em> sblocchi tutte le 9 lezioni + ricevi accesso permanente
        alla piattaforma e a tutti gli aggiornamenti futuri.
      </>
    ),
    promiseTitle: "Cosa ottieni",
    promiseHighlight: "Tutte le 9 lezioni + materiali",
    promisePayoff:
      "Accesso immediato a tutto il percorso, applicabile dalla prima settimana — garanzia 60 giorni.",
    ctaLabel: "Acquista il Corso Completo (€197)",
  },
  "bundle": {
    number: "bundle",
    accessKey: "has_access_l4",
    priceLabel: "€50",
    checkoutEndpoint: "/checkout/bundle-234",
    eyebrow: "Bundle Lezioni 2 + 3 + 4",
    titleLine1: "Hai completato",
    titleHighlight: "la Lezione 1.",
    titleLine2: "Complimenti.",
    subtitle:
      "Pochissimi arrivano fin qui. Il fatto che tu sia ancora con noi significa che hai capito una cosa che molti non vedono.",
    recapTitle: "Nella lezione precedente abbiamo visto",
    recapBody: (
      <>
        Il primo punto: non stai vivendo solo singole crisi —{" "}
        <strong>stai vivendo un copione</strong> che si ripete.
      </>
    ),
    bridgeBody: (
      <>
        Ma come è possibile che questo copione si ripeta ogni volta,{" "}
        <em>anche quando una parte di te sa già come va a finire?</em>
      </>
    ),
    answerBody: (
      <>
        Grazie al <strong>sistema di influenza</strong> che ogni copione attiva.
      </>
    ),
    promiseTitle: "Nella Lezione 2",
    promiseHighlight: "il modello a 6 assi",
    promisePayoff:
      "Cosa ci porta veramente a ripetere il copione — anche quando lo riconosciamo.",
    extraSections: [
      {
        title: "Nella Lezione 3",
        highlight: "la cornice",
        body: (
          <>
            Andiamo ancora più a fondo nel <strong>modello a 6 assi</strong>: spieghiamo{" "}
            <em>su cosa agiscono</em> questi assi. Scoprirai{" "}
            <strong>il livello su cui operano i 6 assi</strong> — e perché senza vederlo
            nessuna tecnica funziona davvero.
          </>
        ),
      },
      {
        title: "Nella Lezione 4",
        highlight: "consolida la nuova cornice",
        body: (
          <>
            Scoprirai <strong>come consolidare la nuova cornice</strong> per non rovinare
            il lavoro svolto in precedenza — il passaggio critico che separa chi cambia
            davvero da chi torna indietro.
          </>
        ),
      },
    ],
    ctaLabel: "Sblocca le Lezioni 2 + 3 + 4 (€50)",
  },
  "lezioni-avanzate": {
    number: "advanced",
    accessKey: "has_access_full_course",
    priceLabel: "€147",
    checkoutEndpoint: "/checkout/bundle-5-9",
    eyebrow: "Le 5 lezioni che mancano",
    titleLine1: "Hai imparato",
    titleHighlight: "cosa fare.",
    titleLine2: "Ora impari come applicarlo.",
    subtitle:
      "Nelle 4 lezioni precedenti hai capito il copione, il modello a 6 assi, la cornice e come consolidarla. Sai cosa cambiare. Ora ti serve il come.",
    recapTitle: "Quello che hai già visto",
    recapBody: (
      <>
        Il <strong>copione</strong>, il <strong>modello a 6 assi</strong>, la{" "}
        <strong>cornice</strong> e come <strong>consolidarla</strong>. Hai le mappe.
      </>
    ),
    bridgeBody: (
      <>
        Ma sapere cosa fare non basta — la differenza la fa il{" "}
        <em>come</em> portarlo a terra nella tua situazione concreta.
      </>
    ),
    answerBody: (
      <>
        Nelle prossime 5 lezioni impari <strong>come mettere a terra</strong> tutto
        quello che hai capito, nella maniera più <strong>efficiente possibile</strong>.
      </>
    ),
    promiseTitle: "Nelle 5 lezioni successive",
    promiseHighlight: "Le insicurezze, i no, la nuova identità, la risoluzione, il protocollo",
    promisePayoff:
      "Dalle insicurezze di tuo figlio (L5), al dire no senza trattative infinite (L6), alla tua nuova identità (L7), alla risoluzione definitiva (L8), fino al protocollo operativo (L9).",
    ctaLabel: "Sblocca le 5 lezioni successive (€147)",
  },
};

function firstName(name) {
  if (!name) return "";
  return String(name).trim().split(/\s+/)[0];
}

export default function SalesLessonPage() {
  const { lesson } = useParams();
  const navigate = useNavigate();
  const copy = useMemo(() => LESSON_COPY[lesson] || null, [lesson]);

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!copy) {
      setLoading(false);
      return;
    }
    if (!getUserToken()) {
      navigate(`/login?next=/vendita/${lesson}`, { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/me`, { headers: authHeader() });
        // If the user already owns this lesson/course, redirect to the appropriate page.
        const ownsKey = copy.accessKey || `has_access_l${copy.number}`;
        if (data?.[ownsKey]) {
          // Map "lesson-like" number to where to send the user if they already own it.
          const target = {
            "full": "/corso/area-membri",
            "bundle": "/corso/lezione/2",
            "advanced": "/corso/area-membri",
            "coaching": "/corso/area-membri",
          }[copy.number] || `/corso/lezione/${copy.number}`;
          navigate(target, { replace: true });
          return;
        }
        setMe(data || null);
      } catch (err) {
        setError(extractApiError(err, "Impossibile caricare il tuo profilo."));
      } finally {
        setLoading(false);
      }
    })();
  }, [copy, lesson, navigate]);

  const handleCheckout = async () => {
    if (!copy || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}${copy.checkoutEndpoint}`,
        {},
        { headers: authHeader() }
      );
      if (data?.url) {
        window.location.replace(data.url);
        return;
      }
      setError("Risposta inattesa dal server. Riprova.");
    } catch (err) {
      setError(extractApiError(err, "Errore nella creazione del carrello."));
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Render ------------------------------------------------------
  if (!copy) {
    return (
      <main
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "var(--np-tertiary)" }}
        data-testid="sales-lesson-invalid"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-8 text-center">
          <h1 className="text-xl mb-2" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
            Pagina non disponibile
          </h1>
          <p className="text-sm" style={{ color: "var(--np-muted)" }}>
            Lezioni supportate: <code>lezione-2</code>, <code>lezione-3</code>, <code>corso-completo</code>, <code>bundle</code>, <code>lezioni-avanzate</code>, <code>coaching-completo</code>.
          </p>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: "var(--np-tertiary)" }}>
        <Loader2 className="animate-spin" size={28} style={{ color: "var(--np-secondary)" }} />
      </main>
    );
  }

  const greetingName = firstName(me?.name);

  return (
    <main
      className="min-h-screen relative overflow-hidden"
      style={{ background: "var(--np-primary)", color: "#ffffff" }}
      data-testid="sales-lesson-page"
    >
      {/* Decorative radial glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 35% at 50% 0%, rgba(213,93,57,0.22), transparent 70%)",
        }}
      />

      <div
        className="relative mx-auto px-6"
        style={{ maxWidth: 760, paddingTop: "clamp(72px, 10vw, 120px)", paddingBottom: "clamp(72px, 10vw, 120px)" }}
      >
        {/* Top accent line */}
        <div className="flex justify-center mb-7">
          <div className="w-12 h-[2px]" style={{ background: "var(--np-secondary)" }} aria-hidden />
        </div>

        {/* Congrats badge */}
        <div className="flex justify-center mb-6">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: "rgba(213,93,57,0.15)", border: "1px solid rgba(213,93,57,0.4)" }}
          >
            <Trophy size={14} style={{ color: "var(--np-secondary)" }} />
            <span className="text-[11px] tracking-[0.25em] uppercase font-semibold" style={{ color: "var(--np-secondary)" }}>
              {copy.eyebrow}
            </span>
          </div>
        </div>

        {/* Headline */}
        <div className="text-center">
          {greetingName && (
            <p
              data-testid="sales-lesson-greeting"
              className="text-base md:text-lg mb-3"
              style={{ color: "rgba(255,255,255,0.75)", fontFamily: "Merriweather, serif" }}
            >
              Ciao <span style={{ color: "#ffffff", fontWeight: 600 }}>{greetingName}</span>,
            </p>
          )}
          <h1
            className="text-3xl md:text-[44px] leading-[1.08] tracking-tight"
            style={{ fontFamily: "Merriweather, serif" }}
          >
            {copy.titleLine1}{" "}
            <span className="italic" style={{ color: "var(--np-secondary)" }}>
              {copy.titleHighlight}
            </span>
            <span className="block mt-2">{copy.titleLine2}</span>
          </h1>
          <p className="mt-7 text-lg md:text-xl leading-[1.7]" style={{ color: "rgba(255,255,255,0.85)" }}>
            {copy.subtitle}
          </p>
        </div>

        {/* Recap card */}
        <section
          className="mt-12 rounded-2xl p-7 md:p-9"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.10)",
            backdropFilter: "blur(6px)",
          }}
        >
          <div className="flex items-start gap-3 mb-5">
            <CheckCircle2 size={20} strokeWidth={2.2} style={{ color: "var(--np-secondary)", flexShrink: 0, marginTop: 3 }} />
            <p className="text-[12px] font-semibold tracking-[0.22em] uppercase" style={{ color: "var(--np-secondary)" }}>
              {copy.recapTitle}
            </p>
          </div>
          <p className="text-lg md:text-xl leading-[1.7] mb-6" style={{ fontFamily: "Merriweather, serif" }}>
            {copy.recapBody}
          </p>

          <div className="mt-7 pt-7 border-t" style={{ borderColor: "rgba(255,255,255,0.10)" }}>
            <p className="text-base md:text-lg leading-[1.75] mb-4" style={{ color: "rgba(255,255,255,0.92)" }}>
              {copy.bridgeBody}
            </p>
            <p className="text-base md:text-lg leading-[1.75]" style={{ color: "rgba(255,255,255,0.92)" }}>
              {copy.answerBody}
            </p>
          </div>
        </section>

        {/* Promise / what you'll learn */}
        <section className="mt-12 text-center">
          <div className="flex justify-center mb-3">
            <Sparkles size={18} style={{ color: "var(--np-secondary)" }} />
          </div>
          <p className="text-[12px] font-semibold tracking-[0.25em] uppercase mb-3" style={{ color: "var(--np-secondary)" }}>
            {copy.promiseTitle}
          </p>
          <h2
            className="text-2xl md:text-[36px] leading-[1.12] tracking-tight"
            style={{ fontFamily: "Merriweather, serif" }}
          >
            <span className="italic" style={{ color: "var(--np-secondary)" }}>
              {copy.promiseHighlight}
            </span>
          </h2>
          <p className="mt-5 text-base md:text-lg leading-[1.7]" style={{ color: "rgba(255,255,255,0.85)" }}>
            {copy.promisePayoff}
          </p>
        </section>

        {/* Extra sections (e.g. bundle: L3 + L4 promises) */}
        {copy.extraSections?.length > 0 && (
          <section className="mt-12 grid gap-5 md:grid-cols-2" data-testid="sales-lesson-extras">
            {copy.extraSections.map((s) => (
              <div
                key={s.title}
                className="rounded-2xl p-6 md:p-7"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <p className="text-[11px] font-semibold tracking-[0.22em] uppercase mb-2" style={{ color: "var(--np-secondary)" }}>
                  {s.title}
                </p>
                <p
                  className="text-xl md:text-2xl leading-[1.18] italic mb-3"
                  style={{ fontFamily: "Merriweather, serif", color: "var(--np-secondary)" }}
                >
                  {s.highlight}
                </p>
                <p className="text-[15px] leading-[1.7]" style={{ color: "rgba(255,255,255,0.85)" }}>
                  {s.body}
                </p>
              </div>
            ))}
          </section>
        )}

        {/* Price + CTA */}
        <section className="mt-14">
          <div
            className="rounded-3xl p-8 md:p-10 text-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(213,93,57,0.20) 0%, rgba(213,93,57,0.05) 100%)",
              border: "1px solid rgba(213,93,57,0.35)",
            }}
          >
            <p className="text-[12px] font-semibold tracking-[0.25em] uppercase mb-2" style={{ color: "var(--np-secondary)" }}>
              Accesso immediato
            </p>
            <p className="text-5xl md:text-6xl font-semibold mb-2" style={{ fontFamily: "Merriweather, serif" }}>
              {copy.priceLabel}
            </p>
            <p className="text-sm mb-7" style={{ color: "rgba(255,255,255,0.7)" }}>
              Una tantum · pagamento sicuro Stripe
            </p>

            <button
              type="button"
              onClick={handleCheckout}
              disabled={submitting}
              data-testid="sales-lesson-cta"
              className="cta-btn inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Apertura carrello…
                </>
              ) : (
                <>
                  {copy.ctaLabel}
                  <ArrowRight size={18} strokeWidth={2.4} />
                </>
              )}
            </button>

            {error && (
              <p className="mt-5 text-sm" data-testid="sales-lesson-error" style={{ color: "#fca5a5" }}>
                {error}
              </p>
            )}

            <div
              className="mt-7 inline-flex items-center gap-2 text-[13px]"
              style={{ color: "rgba(255,255,255,0.7)" }}
            >
              <ShieldCheck size={14} style={{ color: "var(--np-secondary)" }} />
              <span>Garanzia 60 giorni · Soddisfatto o rimborsato</span>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
